/**
 * useTwoFactorSetup - Hook for managing 2FA TOTP enrollment flow
 *
 * Handles the complete 2FA setup process:
 * 1. Enrolling/generating TOTP secret and QR code
 * 2. Verifying the initial code to confirm setup
 * 3. Generating and displaying backup codes
 * 4. Unenrolling/disabling 2FA
 */
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type TwoFactorStep = 'initial' | 'qr_display' | 'verification' | 'backup_codes' | 'complete';

export interface TwoFactorEnrollment {
  /** TOTP URI for QR code generation */
  totpUri: string;
  /** Base32 encoded secret for manual entry */
  secret: string;
  /** QR code data URL (if generated server-side) */
  qrCodeUrl?: string;
}

export interface TwoFactorStatus {
  /** Whether 2FA is currently enabled */
  isEnabled: boolean;
  /** Whether 2FA is verified (setup completed) */
  isVerified: boolean;
  /** Friendly name of the TOTP factor */
  factorName?: string;
  /** Factor ID for management operations */
  factorId?: string;
}

export interface UseTwoFactorSetupReturn {
  /** Current step in the 2FA setup flow */
  currentStep: TwoFactorStep;
  /** Whether the hook is loading data */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Current 2FA status for the user */
  status: TwoFactorStatus | null;
  /** Enrollment data (TOTP URI, secret) */
  enrollment: TwoFactorEnrollment | null;
  /** Generated backup codes */
  backupCodes: string[];
  /** Start the 2FA enrollment process */
  startEnrollment: () => Promise<void>;
  /** Verify the TOTP code to complete enrollment */
  verifyCode: (code: string) => Promise<boolean>;
  /** Disable 2FA for the user */
  disable2FA: () => Promise<boolean>;
  /** Check current 2FA status */
  checkStatus: () => Promise<void>;
  /** Reset the setup flow */
  reset: () => void;
  /** Set current step manually */
  setStep: (step: TwoFactorStep) => void;
}

export function useTwoFactorSetup(): UseTwoFactorSetupReturn {
  const [currentStep, setCurrentStep] = useState<TwoFactorStep>('initial');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [enrollment, setEnrollment] = useState<TwoFactorEnrollment | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [factorId, setFactorId] = useState<string | null>(null);

  /**
   * Check the current 2FA status for the user
   */
  const checkStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      // List all MFA factors for the user
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();

      if (factorsError) {
        console.error('[useTwoFactorSetup] Error listing factors:', factorsError);
        setError(factorsError.message);
        return;
      }

      const totpFactor = factorsData?.totp?.[0];

      if (totpFactor) {
        setStatus({
          isEnabled: true,
          isVerified: totpFactor.status === 'verified',
          factorName: totpFactor.friendly_name || 'Authenticator App',
          factorId: totpFactor.id,
        });
        setFactorId(totpFactor.id);

        if (totpFactor.status === 'verified') {
          setCurrentStep('complete');
        }
      } else {
        setStatus({
          isEnabled: false,
          isVerified: false,
        });
        setCurrentStep('initial');
      }
    } catch (err) {
      console.error('[useTwoFactorSetup] Unexpected error:', err);
      setError('Failed to check 2FA status');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Start the 2FA enrollment process
   */
  const startEnrollment = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Enroll a new TOTP factor
      const { data: enrollData, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });

      if (enrollError) {
        console.error('[useTwoFactorSetup] Enrollment error:', enrollError);
        setError(enrollError.message);
        return;
      }

      if (enrollData) {
        setEnrollment({
          totpUri: enrollData.totp.uri,
          secret: enrollData.totp.secret,
          qrCodeUrl: enrollData.totp.qr_code,
        });
        setFactorId(enrollData.id);
        setCurrentStep('qr_display');
      }
    } catch (err) {
      console.error('[useTwoFactorSetup] Unexpected enrollment error:', err);
      setError('Failed to start 2FA enrollment');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify the TOTP code to complete enrollment
   */
  const verifyCode = useCallback(async (code: string): Promise<boolean> => {
    if (!factorId) {
      setError('No enrollment in progress');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Create a challenge for verification
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) {
        console.error('[useTwoFactorSetup] Challenge error:', challengeError);
        setError(challengeError.message);
        return false;
      }

      // Verify the code
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });

      if (verifyError) {
        console.error('[useTwoFactorSetup] Verification error:', verifyError);
        setError('Invalid code. Please try again.');
        return false;
      }

      if (verifyData) {
        // Generate backup codes
        const generatedCodes = generateBackupCodes();
        setBackupCodes(generatedCodes);

        // Update status
        setStatus({
          isEnabled: true,
          isVerified: true,
          factorName: 'Authenticator App',
          factorId,
        });

        setCurrentStep('backup_codes');
        return true;
      }

      return false;
    } catch (err) {
      console.error('[useTwoFactorSetup] Unexpected verification error:', err);
      setError('Failed to verify code');
      return false;
    } finally {
      setLoading(false);
    }
  }, [factorId]);

  /**
   * Disable 2FA for the user
   */
  const disable2FA = useCallback(async (): Promise<boolean> => {
    if (!factorId && !status?.factorId) {
      setError('No 2FA factor to disable');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const targetFactorId = factorId || status?.factorId;

      const { error: unenrollError } = await supabase.auth.mfa.unenroll({
        factorId: targetFactorId!,
      });

      if (unenrollError) {
        console.error('[useTwoFactorSetup] Unenroll error:', unenrollError);
        setError(unenrollError.message);
        return false;
      }

      // Reset state
      setStatus({
        isEnabled: false,
        isVerified: false,
      });
      setEnrollment(null);
      setBackupCodes([]);
      setFactorId(null);
      setCurrentStep('initial');

      return true;
    } catch (err) {
      console.error('[useTwoFactorSetup] Unexpected unenroll error:', err);
      setError('Failed to disable 2FA');
      return false;
    } finally {
      setLoading(false);
    }
  }, [factorId, status?.factorId]);

  /**
   * Reset the setup flow
   */
  const reset = useCallback(() => {
    setCurrentStep('initial');
    setError(null);
    setEnrollment(null);
    setBackupCodes([]);
    // Don't reset status or factorId - those reflect actual state
  }, []);

  /**
   * Set current step manually
   */
  const setStep = useCallback((step: TwoFactorStep) => {
    setCurrentStep(step);
  }, []);

  return {
    currentStep,
    loading,
    error,
    status,
    enrollment,
    backupCodes,
    startEnrollment,
    verifyCode,
    disable2FA,
    checkStatus,
    reset,
    setStep,
  };
}

/**
 * Generate random backup codes
 * Format: XXXX-XXXX (8 alphanumeric characters)
 */
function generateBackupCodes(count: number = 10): string[] {
  const codes: string[] = [];
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude confusing characters (I, O, 0, 1)

  for (let i = 0; i < count; i++) {
    let code = '';
    for (let j = 0; j < 8; j++) {
      if (j === 4) code += '-';
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    codes.push(code);
  }

  return codes;
}
